// Spec 41 — best-effort extractie van de contract-startdatum uit de tekst van
// een arbeidscontract-PDF. Gebruikt door het migratiescript om de startDatum van
// een gemigreerd EmploymentContract te bepalen (fallback: employee.startDate of
// createdAt). Pure functie zonder I/O zodat hij los te unit-testen is; de
// PDF-tekst-extractie (pdf-parse) gebeurt in het script.

const NL_MAANDEN: Record<string, number> = {
  januari: 1, jan: 1,
  februari: 2, feb: 2,
  maart: 3, mrt: 3,
  april: 4, apr: 4,
  mei: 5,
  juni: 6, jun: 6,
  juli: 7, jul: 7,
  augustus: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  oktober: 10, okt: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,
}

// Signaalfrases die in een NL-arbeidscontract aan de ingangsdatum voorafgaan
// (meest specifieke eerst zodat die wint).
const FRASES = [
  'datum indiensttreding',
  'datum van indiensttreding',
  'in dienst per',
  'datum in dienst',
  'indiensttreding',
  'met ingang van',
  'aanvang van het dienstverband',
  'aanvang dienstverband',
  'ingangsdatum',
  'startdatum',
]

function geldigeDatum(dag: number, maand: number, jaar: number): boolean {
  if (maand < 1 || maand > 12) return false
  if (dag < 1 || dag > 31) return false
  const huidigJaar = new Date().getFullYear()
  if (jaar < 1980 || jaar > huidigJaar + 1) return false
  // Echte kalenderdag-check (vangt 31-02 e.d.).
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  return (
    d.getUTCFullYear() === jaar &&
    d.getUTCMonth() === maand - 1 &&
    d.getUTCDate() === dag
  )
}

function toIso(dag: number, maand: number, jaar: number): string {
  return `${jaar}-${String(maand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
}

/** Zoek in een tekstvenster naar de eerste plausibele datum (numeriek of NL-tekst). */
function datumInVenster(venster: string): string | null {
  // Numeriek: d-m-jjjj / d/m/jjjj / d.m.jjjj
  const num = venster.match(/(\d{1,2})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{4})/)
  if (num) {
    const dag = Number(num[1])
    const maand = Number(num[2])
    const jaar = Number(num[3])
    if (geldigeDatum(dag, maand, jaar)) return toIso(dag, maand, jaar)
  }
  // Tekstueel: d <maand> jjjj
  const txt = venster.match(/(\d{1,2})\s+([a-z]+)\.?\s+(\d{4})/)
  if (txt) {
    const dag = Number(txt[1])
    const maand = NL_MAANDEN[txt[2]]
    const jaar = Number(txt[3])
    if (maand && geldigeDatum(dag, maand, jaar)) return toIso(dag, maand, jaar)
  }
  return null
}

/**
 * Probeer de contract-startdatum uit de PDF-tekst te lezen. Zoekt een datum vlak
 * na een NL-contractfrase ("in dienst per", "ingangsdatum", ...). Geeft
 * 'yyyy-mm-dd' of null wanneer niets plausibels gevonden is (dan valt het
 * migratiescript terug op employee.startDate / createdAt). Bewust conservatief:
 * alleen datums binnen een plausibele band en vlak na een frase, nooit een
 * willekeurige datum ergens in het document.
 */
export function extractContractStartDatum(tekst: string | null | undefined): string | null {
  if (!tekst) return null
  const genormaliseerd = tekst.toLowerCase().replace(/\s+/g, ' ')
  for (const frase of FRASES) {
    let vanaf = 0
    for (;;) {
      const idx = genormaliseerd.indexOf(frase, vanaf)
      if (idx === -1) break
      const start = idx + frase.length
      const iso = datumInVenster(genormaliseerd.slice(start, start + 40))
      if (iso) return iso
      vanaf = start
    }
  }
  return null
}
